/*
 * Die Datenquelle hinter den Miniaturen der Palette.
 *
 * WARUM UEBERHAUPT EINE QUELLE. Ein Widget ohne Datenpunkt zeigt "kein
 * Wert", und eine Palette aus zweiundzwanzig solchen Kaesten waere genau so
 * wenig aussagekraeftig wie die Textliste, die sie ersetzt. Die Miniatur
 * soll zeigen, wie das Ding aussieht, wenn es arbeitet.
 *
 * WARUM `demo/`. Dieselbe Sperre wie auf der Katalogseite
 * (`pages/HmiPreviewPage.tsx`): der Live-Adapter (`hmi/SignalRSource.ts`)
 * loest Schluessel mit diesem Praefix NIE auf. Ein Beispiel-Widget, das
 * versehentlich in ein echtes Projekt geriete, zeigte damit sichtbar ins
 * Leere, statt gegen eine echte Anlage zu schreiben. Regeln dazu in
 * `hmi/bindingKey.ts`.
 *
 * WARUM DIE WERTE STILLSTEHEN. Die Katalogseite laesst einen Sim-Lauf im
 * Sekundentakt mitlaufen, weil sie zum Beurteilen da ist. Die Palette ist
 * zum Auswaehlen da. Ein Takt, der zweiundzwanzig Widgets gleichzeitig neu
 * zeichnen laesst, waehrend der Autor nebenan ein Bild baut, kostet
 * Bildwiederholrate an genau der Stelle, an der sie gebraucht wird: beim
 * Ziehen auf der Flaeche. Die Werte werden also einmal gesetzt und bleiben
 * stehen. Bewegung in der Palette ist kein Ziel, Erkennbarkeit schon.
 *
 * DIE KURVEN SIND KEINE ZIERDE. Verlaufsstrich und Trend holen ihre Punkte
 * ueber `getHistory`, nicht aus dem laufenden Abo. Ohne rueckdatierte Werte
 * zeigten beide eine leere Flaeche, und das ist die eine Miniatur, die
 * nichts erklaert. Deshalb legt {@link seedCurve} fuer die betroffenen
 * Punkte einen Tagesverlauf in den Ringpuffer.
 *
 * DIE DOPPELUNG ZUR KATALOGSEITE IST BEWUSST. Die Startwerte stehen dort
 * ein zweites Mal. Sie zusammenzulegen hiesse, eine Seite unter `pages/` zu
 * einem Modul umzubauen, von dem der Editor abhaengt; das ist eine
 * Abhaengigkeitsrichtung, die man spaeter bereut. Wer hier einen Punkt
 * ergaenzt, ergaenzt ihn nicht dort: die beiden Listen duerfen
 * auseinanderlaufen, solange jede fuer sich vollstaendig ist.
 */

import { InMemoryPointBindingSource } from '../../InMemorySource';

/** Ein Tag rueckwaerts, so weit blicken Verlaufsstrich und Trend zurueck. */
const WINDOW_MS = 24 * 60 * 60_000;

/** Stuetzstellen je Kurve. 48 heisst alle halbe Stunde: genug fuer eine
 *  erkennbare Linie, wenig genug, dass das Anlegen der Quelle nicht
 *  auffaellt. */
const CURVE_POINTS = 48;

/**
 * Die Quelle fuer alle Miniaturen einer Palette.
 *
 * EINE fuer alle und nicht eine je Eintrag: die Widgets abonnieren nur,
 * sie schreiben nichts (die Miniaturen sind bedienungsblind), und zwei
 * Dutzend Instanzen mit denselben Werten waeren zwei Dutzend Ringpuffer
 * ohne Gegenwert.
 */
export function createPalettePreviewSource(): InMemoryPointBindingSource {
  const source = new InMemoryPointBindingSource({ echoLatencyMs: 90 });

  // Kurven zuerst: sie hinterlassen als letzten Punkt den Jetzt-Wert, und
  // die Seeds darunter wuerden ihn sonst ueberschreiben.
  seedCurve(source, 'demo/room-temperature', 21.4, 0.6, 20);
  seedCurve(source, 'demo/supply-temperature', 42.1, 6, 8);
  seedCurve(source, 'demo/room-setpoint', 21.5, 0.5, 30);

  source.seed('demo/humidity', 47);
  // Bogenskala, Zeitprogramm, Kennlinie und Speicher (15.10.2026).
  source.seed('demo/zsp', '06:00-22:00, --, --');
  source.seed('demo/slope', 0.5);
  source.seed('demo/dhw-top', 52);
  source.seed('demo/el-power', 1.2);
  source.seed('demo/heat-power', 4.9);
  source.seed('demo/months', [3.4, 3.5, 3.8, 4.2, 4.6, 4.9, 5.0, 4.9, 4.5, 4.0, null, null]);
  source.seed('demo/year-elec', 2410);
  source.seed('demo/year-heat', 9180);
  source.seed('demo/pump-load', 0.62);
  source.seed('demo/mode', 'heating');
  source.seed('demo/compressor', 'on');
  source.seed('demo/valve-open', false);
  source.seed('demo/fan-stage', 2);
  source.seed('demo/energy-kwh', 128740);
  source.seed('demo/runtime-hours', 15320);
  source.seed('demo/cop', 3.85);
  source.seed('demo/alarm-count', 0);

  source.seed('demo/hp/electrical-power', 2450);
  source.seed('demo/hp/thermal-power', 9800);
  source.seed('demo/hp/state', 'Running');
  source.seed('demo/hp/flow', 43.2);
  source.seed('demo/hp/return', 37.6);
  source.seed('demo/hp/starts-today', 4);
  source.seed('demo/hp/runtime-today', 9720);
  source.seed('demo/hp/runtime-lifetime', 55152000);

  source.seed('demo/hc/valve', 62);
  source.seed('demo/hc/pump', true);

  source.seed('demo/hw/top', 54.3);
  source.seed('demo/hw/mid', 47.8);
  source.seed('demo/hw/bottom', 35.1);
  source.seed('demo/hw/setpoint', 52);
  source.seed('demo/hw/state', 'Idle');
  source.seed(
    'demo/hw/next-legionella',
    new Date(Date.now() + 4 * 24 * 3600 * 1000).toISOString(),
  );

  source.seed('demo/room/humidity', 46);
  source.seed('demo/room/presence', true);
  source.seed('demo/room/window', false);

  source.seed('demo/outside/temp', -3.2);
  source.seed('demo/outside/attenuated', -0.8);
  source.seed('demo/outside/wind', 14.5);
  source.seed('demo/outside/icon', 'schnee');

  source.seed('demo/pv/production', 1820);
  source.seed('demo/pv/household', 2600);
  source.seed('demo/pv/grid', -780);
  source.seed('demo/pv/battery-soc', 68);
  source.seed('demo/pv/battery-power', -320);
  source.seed('demo/pv/daily-production', 24300);
  source.seed('demo/pv/daily-self', 18100);
  source.seed('demo/pv/daily-feed', 6200);
  source.seed('demo/pv/daily-grid', 4500);

  return source;
}

/**
 * Einen Tagesverlauf in den Ringpuffer eines Punkts legen.
 *
 * Die letzte Stuetzstelle traegt die Jetzt-Zeit, und das ist kein Detail:
 * `hmi/quality.ts` leitet "veraltet" aus dem ALTER des letzten Werts ab.
 * Ein Verlauf, der vor einer Stunde endet, liesse jede Miniatur blass und
 * mit Altershinweis erscheinen, also ausgerechnet im Ausnahmezustand.
 */
function seedCurve(
  source: InMemoryPointBindingSource,
  pointId: string,
  centre: number,
  amplitude: number,
  period: number,
): void {
  const now = Date.now();
  const step = WINDOW_MS / CURVE_POINTS;
  for (let i = CURVE_POINTS; i >= 0; i -= 1) {
    const value = centre + Math.sin((CURVE_POINTS - i) / period) * amplitude;
    source.publish(pointId, Math.round(value * 100) / 100, 'good', {
      receivedAt: now - i * step,
    });
  }
}
